// Native Authentication Service for iOS/Android
// Uses @capacitor-firebase/authentication for ALL auth methods
// IMPORTANT: Static import — this is how it worked in January. Do NOT change to dynamic require().

import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { 
  auth, 
  signInWithCredential,
  GoogleAuthProvider,
  signInWithEmailAndPassword as webSignInWithEmail,
  createUserWithEmailAndPassword as webCreateUserWithEmail,
  sendPasswordResetEmail as webSendPasswordReset,
  signInWithPhoneNumber as webSignInWithPhone,
  RecaptchaVerifier,
  signInWithPopup,
  googleProvider,
  signOut as webSignOut
} from './firebase';

// Check if running on native platform
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

// Get current platform
export const getPlatform = () => {
  return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
};

// Timeout wrapper - prevents native calls from hanging forever
const withTimeout = (promise, ms, label) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s. Please try again.`));
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
};

// Store for phone verification
let phoneVerificationId = null;

// Initialize (no-op now, kept for compatibility)
export const initializeGoogleAuth = async () => {
  console.log('[NativeAuth] initializeGoogleAuth called, platform:', getPlatform());
};

// ============ GOOGLE SIGN IN ============
export const signInWithGoogle = async () => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Google Sign-In on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    // NATIVE ONLY — NO web SDK fallbacks (signInWithPopup hangs in WebViews)
    const MAX_RETRIES = 2;
    let lastError = null;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`[NativeAuth] Starting native Google Sign-In (attempt ${attempt}/${MAX_RETRIES})...`);
        
        // Force sign out FIRST to clear any cached Google session
        // This ensures picker shows every time (instead of auto-login)
        try {
          await FirebaseAuthentication.signOut();
          console.log('[NativeAuth] Pre-signIn signOut done (force picker)');
        } catch (_) {}
        
        // Force Google account picker to show every time (prevents silent auto-login)
        const signInPromise = FirebaseAuthentication.signInWithGoogle({
          customParameters: [
            { key: 'prompt', value: 'select_account' }
          ]
        });
        console.log('[NativeAuth] signInWithGoogle called (select_account prompt), awaiting with 45s timeout...');
        
        // iOS cold start can be slow — 45s timeout (was 30s)
        const result = await withTimeout(signInPromise, 45000, 'Google Sign-In');
        console.log('[NativeAuth] Google sign-in result:', result?.user?.email);
        
        if (!result?.user) {
          throw new Error('Google Sign-In returned no user. Please try again.');
        }
        
        // Sync credential to web SDK (best effort, non-blocking)
        if (result.credential?.idToken) {
          try {
            const credential = GoogleAuthProvider.credential(
              result.credential.idToken,
              result.credential.accessToken
            );
            await signInWithCredential(auth, credential);
            console.log('[NativeAuth] Web SDK sync successful');
          } catch (webSdkError) {
            console.log('[NativeAuth] Web SDK sync skipped (non-fatal):', webSdkError.message);
          }
        }
        
        // Get ID token for API calls
        let token = null;
        try {
          const tokenResult = await withTimeout(FirebaseAuthentication.getIdToken(), 10000, 'Get ID Token');
          token = tokenResult.token;
        } catch (tokenErr) {
          console.log('[NativeAuth] getIdToken failed, using credential token:', tokenErr.message);
          token = result.credential?.idToken || null;
        }
        
        return {
          user: {
            uid: result.user?.uid,
            email: result.user?.email,
            displayName: result.user?.displayName,
            photoURL: result.user?.photoUrl,
            phoneNumber: result.user?.phoneNumber,
            getIdToken: async () => token
          }
        };
      } catch (error) {
        lastError = error;
        console.error(`[NativeAuth] Native Google Sign-In error (attempt ${attempt}):`, error);
        
        // Don't retry if user cancelled or popup closed
        if (error.message?.includes('popup_closed') || error.message?.includes('cancelled') || 
            error.code === '12501' || error.message?.includes('user cancelled')) {
          throw { code: 'auth/popup-closed-by-user', message: 'Sign-in cancelled' };
        }
        
        // Retry after brief delay for timeout/network errors
        if (attempt < MAX_RETRIES && (error.message?.includes('timed out') || error.message?.includes('network'))) {
          console.log(`[NativeAuth] Retrying Google Sign-In in 2s...`);
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        
        throw error;
      }
    }
    
    throw lastError || new Error('Google Sign-In failed after retries');
  } else {
    // WEB: Use popup
    console.log('[NativeAuth] Using web Google Sign-In popup');
    const result = await signInWithPopup(auth, googleProvider);
    return {
      user: result.user,
      credential: null
    };
  }
};

// ============ EMAIL/PASSWORD SIGN IN ============
export const signInWithEmail = async (email, password) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Email Sign-In on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    // NATIVE ONLY — NO web SDK fallbacks
    try {
      console.log('[NativeAuth] Starting native Email Sign-In...');
      const result = await withTimeout(
        FirebaseAuthentication.signInWithEmailAndPassword({ email, password }),
        15000, 'Email Sign-In'
      );
      console.log('[NativeAuth] Email sign-in success:', result?.user?.email);
      
      const tokenResult = await withTimeout(FirebaseAuthentication.getIdToken(), 10000, 'Get ID Token');
      
      return {
        user: {
          uid: result.user?.uid,
          email: result.user?.email,
          displayName: result.user?.displayName,
          photoURL: result.user?.photoUrl,
          phoneNumber: result.user?.phoneNumber,
          getIdToken: async () => tokenResult.token
        }
      };
    } catch (error) {
      console.error('[NativeAuth] Native Email Sign-In error:', error);
      throw error;
    }
  } else {
    const result = await webSignInWithEmail(auth, email, password);
    return { user: result.user };
  }
};

// ============ EMAIL/PASSWORD SIGN UP ============
export const signUpWithEmail = async (email, password) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Email Sign-Up on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    // NATIVE ONLY — NO web SDK fallbacks
    try {
      console.log('[NativeAuth] Starting native Email Sign-Up...');
      const result = await withTimeout(
        FirebaseAuthentication.createUserWithEmailAndPassword({ email, password }),
        15000, 'Email Sign-Up'
      );
      console.log('[NativeAuth] Email sign-up success:', result?.user?.email);
      
      const tokenResult = await withTimeout(FirebaseAuthentication.getIdToken(), 10000, 'Get ID Token');
      
      return {
        user: {
          uid: result.user?.uid,
          email: result.user?.email,
          displayName: result.user?.displayName,
          photoURL: result.user?.photoUrl,
          phoneNumber: result.user?.phoneNumber,
          getIdToken: async () => tokenResult.token
        }
      };
    } catch (error) {
      console.error('[NativeAuth] Native Email Sign-Up error:', error);
      throw error;
    }
  } else {
    const result = await webCreateUserWithEmail(auth, email, password);
    return { user: result.user };
  }
};

// ============ PASSWORD RESET ============
export const sendPasswordReset = async (email) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Password Reset on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    await withTimeout(FirebaseAuthentication.sendPasswordResetEmail({ email }), 15000, 'Password Reset');
  } else {
    await webSendPasswordReset(auth, email);
  }
};

// ============ PHONE SIGN IN ============
export const signInWithPhone = async (phoneNumber) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Phone Sign-In on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    try {
      console.log('[NativeAuth] Starting native phone auth for:', phoneNumber);
      
      try { FirebaseAuthentication.removeAllListeners(); } catch (e) {}
      
      let resolvePromise, rejectPromise;
      const listenerPromise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });
      
      const timeout = setTimeout(() => {
        console.log('[NativeAuth] Phone verification timed out after 2 minutes');
        try { FirebaseAuthentication.removeAllListeners(); } catch (e) {}
        rejectPromise(new Error('Phone verification timed out. Please try again.'));
      }, 120000);
      
      console.log('[NativeAuth] Setting up phone verification listeners...');
      
      FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
        console.log('[NativeAuth] phoneCodeSent event received:', event);
        clearTimeout(timeout);
        phoneVerificationId = event.verificationId;
        resolvePromise({ verificationId: event.verificationId, isNative: true });
      });
      
      FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
        console.log('[NativeAuth] phoneVerificationFailed event received:', event);
        clearTimeout(timeout);
        try { FirebaseAuthentication.removeAllListeners(); } catch (e) {}
        rejectPromise(new Error(event.message || 'Phone verification failed'));
      });
      
      FirebaseAuthentication.addListener('phoneVerificationCompleted', async (event) => {
        console.log('[NativeAuth] phoneVerificationCompleted event received (auto-verify):', event);
        clearTimeout(timeout);
        try {
          const tokenResult = await FirebaseAuthentication.getIdToken();
          resolvePromise({ autoVerified: true, isNative: true, user: { getIdToken: async () => tokenResult.token } });
        } catch (e) {
          resolvePromise({ verificationId: event.verificationId, isNative: true });
        }
      });
      
      console.log('[NativeAuth] Calling signInWithPhoneNumber...');
      FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber });
      console.log('[NativeAuth] signInWithPhoneNumber called, waiting for listener...');
      
      return await listenerPromise;
    } catch (error) {
      console.error('[NativeAuth] Phone Sign-In error:', error);
      try { FirebaseAuthentication.removeAllListeners(); } catch (e) {}
      throw error;
    }
  } else {
    throw new Error('Use web phone auth with reCAPTCHA');
  }
};

// ============ VERIFY PHONE CODE ============
export const verifyPhoneCode = async (verificationCode, verificationId) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Verifying phone code on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    try {
      const vid = verificationId || phoneVerificationId;
      if (!vid) throw new Error('No verification ID');
      
      const result = await withTimeout(
        FirebaseAuthentication.confirmVerificationCode({ verificationId: vid, verificationCode }),
        15000, 'Verify OTP'
      );
      const tokenResult = await withTimeout(FirebaseAuthentication.getIdToken(), 10000, 'Get ID Token');
      FirebaseAuthentication.removeAllListeners();
      phoneVerificationId = null;
      
      return {
        user: {
          uid: result.user?.uid,
          email: result.user?.email,
          displayName: result.user?.displayName,
          photoURL: result.user?.photoUrl,
          phoneNumber: result.user?.phoneNumber,
          getIdToken: async () => tokenResult.token
        }
      };
    } catch (error) {
      try { FirebaseAuthentication.removeAllListeners(); } catch (e) {}
      throw error;
    }
  } else {
    throw new Error('Use web phone auth confirmation');
  }
};

// ============ GET CURRENT USER ============
export const getCurrentUser = async () => {
  const platform = getPlatform();
  
  if (platform === 'ios' || platform === 'android') {
    try {
      const result = await FirebaseAuthentication.getCurrentUser();
      if (result.user) {
        const tokenResult = await FirebaseAuthentication.getIdToken();
        return {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoUrl,
          phoneNumber: result.user.phoneNumber,
          getIdToken: async () => tokenResult.token
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  } else {
    return auth.currentUser;
  }
};

// ============ SIGN OUT ============
export const signOut = async () => {
  const platform = getPlatform();
  console.log(`[NativeAuth] Sign out on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    try {
      await FirebaseAuthentication.signOut();
    } catch (e) {
      console.log('[NativeAuth] Native signOut error:', e);
    }
  }
  
  // Always sign out from web SDK too
  try {
    await webSignOut(auth);
  } catch (e) {
    console.log('[NativeAuth] Web signOut error:', e);
  }
};

// ============ PHONE OTP (Aliases for compatibility) ============
export const sendPhoneOTP = async (phoneNumber, recaptchaContainerId = 'recaptcha-container') => {
  const platform = getPlatform();
  console.log(`[NativeAuth] sendPhoneOTP on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    return await signInWithPhone(phoneNumber);
  } else {
    // Web: use reCAPTCHA
    try {
      const container = document.getElementById(recaptchaContainerId);
      if (!container) throw new Error('reCAPTCHA container not found');
      
      const recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
        size: 'invisible',
        callback: () => console.log('reCAPTCHA solved')
      });
      
      const confirmationResult = await webSignInWithPhone(auth, phoneNumber, recaptchaVerifier);
      return {
        isNative: false,
        confirmationResult
      };
    } catch (error) {
      console.error('[NativeAuth] Web Phone OTP error:', error);
      throw error;
    }
  }
};

// Confirm phone OTP
export const confirmPhoneOTP = async (code, verificationIdOrResult) => {
  const platform = getPlatform();
  console.log(`[NativeAuth] confirmPhoneOTP on platform: ${platform}`);
  
  if (platform === 'ios' || platform === 'android') {
    const vid = typeof verificationIdOrResult === 'string' 
      ? verificationIdOrResult 
      : verificationIdOrResult?.verificationId || phoneVerificationId;
    return await verifyPhoneCode(code, vid);
  } else {
    // Web - use confirmationResult
    if (verificationIdOrResult?.confirmationResult) {
      const result = await verificationIdOrResult.confirmationResult.confirm(code);
      return { user: result.user };
    }
    throw new Error('No confirmation result for web');
  }
};

const NativeAuth = {
  isNativePlatform,
  getPlatform,
  initializeGoogleAuth,
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  createUserWithEmail: signUpWithEmail,
  sendPasswordReset,
  signInWithPhone,
  sendPhoneOTP,
  confirmPhoneOTP,
  verifyPhoneCode,
  signOut,
  getCurrentUser
};

export default NativeAuth;
